import React, { useEffect, useState } from 'react';
import { Alert, Spin } from 'antd';
import { ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../useAuth';
import { ecrSubscriptionApi } from '../api';
import { EcrSubscription } from '../types';

interface SubscriptionStatusBannerProps {
  className?: string;
}

const SubscriptionStatusBanner: React.FC<SubscriptionStatusBannerProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { token, role } = useAuth();
  const [subscription, setSubscription] = useState<EcrSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch for MERCHANT role
    if (role !== 'MERCHANT' || !token) {
      setLoading(false);
      return;
    }

    fetchSubscriptionStatus();
  }, [token, role]);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ecrSubscriptionApi.getMySubscription(token!);
      setSubscription(data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError(t('merchant.subscription.errorLoading') || 'Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything for non-merchant roles
  if (role !== 'MERCHANT') {
    return null;
  }

  // Don't show anything while loading
  if (loading) {
    return (
      <div className={`flex justify-center items-center py-2 ${className}`}>
        <Spin size="small" />
      </div>
    );
  }

  // Don't show anything if there's an error or no subscription
  if (error || !subscription) {
    return null;
  }

  // Only show banner for PENDING or REJECTED status
  const status = subscription.status?.toUpperCase();
  if (status !== 'PENDING' && status !== 'REJECTED') {
    return null;
  }

  const getStatusConfig = () => {
    switch (status) {
      case 'PENDING':
        return {
          type: 'warning' as const,
          icon: <ClockCircleOutlined />,
          message: t('merchant.subscription.pendingTitle') || 'ECR Subscription Pending',
          description: t('merchant.subscription.pendingMessage', { 
            provider: subscription.ecrProviderName || 'ECR Provider' 
          }) || `Your subscription request to ${subscription.ecrProviderName || 'ECR Provider'} is awaiting approval.`,
        };
      case 'REJECTED':
        return {
          type: 'error' as const,
          icon: <CloseCircleOutlined />,
          message: t('merchant.subscription.rejectedTitle') || 'ECR Subscription Rejected',
          description: subscription.rejectionReason 
            ? t('merchant.subscription.rejectedMessageWithReason', { 
                provider: subscription.ecrProviderName || 'ECR Provider',
                reason: subscription.rejectionReason 
              }) || `Your subscription request to ${subscription.ecrProviderName || 'ECR Provider'} was declined. Reason: ${subscription.rejectionReason}`
            : t('merchant.subscription.rejectedMessage', { 
                provider: subscription.ecrProviderName || 'ECR Provider' 
              }) || `Your subscription request to ${subscription.ecrProviderName || 'ECR Provider'} was declined.`,
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) {
    return null;
  }

  return (
    <Alert
      className={`mb-4 ${className}`}
      type={config.type}
      icon={config.icon}
      message={config.message}
      description={config.description}
      showIcon
      banner
    />
  );
};

export default SubscriptionStatusBanner;
